import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { LANGS } from "../i18n";
import { useStore } from "../context/StoreContext";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

const Header = () => {
  const { t, lang, setLang, cartCount, setCartOpen } = useStore();
  const [open, setOpen] = useState(false);
  const links = [
    ["product", t.nav.product],
    ["ingredients", t.nav.ingredients],
    ["reviews", t.nav.reviews],
    ["faq", t.nav.faq],
    ["contact", t.nav.contact],
  ];
  const go = (id) => { setOpen(false); setTimeout(() => scrollTo(id), 320); };

  return (
    <header className="fixed left-0 right-0 top-0 z-[70]">
      <div className="bg-[var(--ink)] py-1.5 text-center text-[11px] font-semibold tracking-wide text-white sm:text-xs" data-testid="announcement-bar">
        {t.announcement}
      </div>
      <div className="header-glass border-b border-[var(--line)]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <button data-testid="logo-button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center" aria-label="MKB Performance">
            <img src="/images/logo-dark.png" alt="MKB Performance" className="h-9 w-auto sm:h-10" data-testid="logo-image" />
          </button>

          <nav className="hidden items-center gap-6 md:flex">
            {links.map(([id, label]) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)} className="text-sm font-semibold text-[var(--ink)]/80 transition-colors hover:text-[var(--ink)]">
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center border border-[var(--line)] bg-white/60" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`px-2 py-1 text-[11px] font-bold transition-colors ${lang === l.code ? "bg-[var(--ink)] text-white" : "text-[var(--muted)] hover:text-[var(--ink)]"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button data-testid="cart-button" onClick={() => setCartOpen(true)} className="relative flex h-10 w-10 items-center justify-center border border-[var(--line)] bg-white/60" aria-label={t.nav.cart}>
              <ShoppingBag className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="accent-bg absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center px-1 text-[10px] font-bold" style={{ color: "var(--accent-contrast)" }} data-testid="cart-count-badge">
                  {cartCount}
                </span>
              )}
            </button>
            <button data-testid="menu-button" onClick={() => setOpen((o) => !o)} className="flex h-10 w-10 items-center justify-center border border-[var(--line)] bg-white/60 md:hidden" aria-label={t.nav.menu}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden border-t border-[var(--line)] md:hidden"
              data-testid="mobile-menu"
            >
              <div className="flex flex-col px-4 py-2">
                {links.map(([id, label]) => (
                  <button key={id} data-testid={`mobile-nav-${id}`} onClick={() => go(id)} className="border-b border-[var(--line)] py-3 text-left font-display text-2xl last:border-b-0">
                    {label}
                  </button>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;
